import { useReactiveVar } from "@apollo/client";

import useRecordUserGoal, { GoalType } from "./useRecordUserGoal";

import {
  useRecommendationsRemovePlayerFromRecommendationsMutation as useRemovePlayerRecommendationMutation,
  useWatchlistRemovePlayerFromWatchlistMutation,
  Watchlist,
  WatchlistListMyUserWatchlistsDocument,
  WatchlistListMyUserWatchlistsQuery,
  WatchlistPlayerFragment,
} from "@/generated/graphql";
import { playerDetailVar } from "@/stores/player";
import {
  addOrRemovePlayerInWatchlist,
  changeStatusPlayerInWatchlist,
} from "@/utils/handleWatchlistPlayers";

export const useDeletePlayer = () => {
  const infoTemp = useReactiveVar(playerDetailVar);
  const playerDetail = infoTemp?.player;
  const recordRemovePlayerGoal = useRecordUserGoal({
    goalType: GoalType.RemovePlayerFromWatchlist,
    value: playerDetail?.id ?? "",
  });
  const [removePlayer] = useWatchlistRemovePlayerFromWatchlistMutation();
  const deletePlayer = (activeWatchlist?: Watchlist) => {
    // Guard against a missing watchlist: without a watchlistId the mutation
    // is malformed and the cache write below reads off a null watchlist. The
    // callers always pass the active watchlist, but defend the core add/remove
    // path against the prod ApolloError "Cannot read properties of null
    // (reading 'watchlistId')" (SE-5235) rather than firing a bad mutation.
    if (!activeWatchlist?.id || !playerDetail?.id) {
      return Promise.resolve();
    }

    return removePlayer({
      onCompleted: () => {
        recordRemovePlayerGoal();
        playerDetailVar(undefined);
      },
      onError: error => {
        // Surface the typed GraphQL error cleanly instead of letting a raw
        // ApolloError bubble (SE-5235). The mutation already rejects, so the
        // caller's promise chain can react; we just stop the unhandled throw.
        console.error("[Watchlist] Remove player failed:", error.message);
      },
      variables: {
        input: {
          playerId: playerDetail.id,
          watchlistId: activeWatchlist.id,
        },
      },
      // No optimisticResponse: removePlayerFromWatchlist selects the full
      // WatchlistWatchlist (per-player season metrics + bioData), but the
      // watchlist available here is the lightweight meta shape (id-only
      // players). Casting it to Watchlist produced an incomplete optimistic
      // write — when the watchlist was undefined it threw the prod
      // ApolloError "Cannot read properties of null (reading 'watchlistId')",
      // and even when present it flooded Apollo "Missing field" (error 13)
      // warnings. Per PROJECT_RULES (SE-5058), an optimisticResponse must be
      // complete against the full selection set or omitted entirely; the
      // update() callback below reconciles membership on resolve instead.
      update: async (cache, { data: dataCache, errors }) => {
        const data: WatchlistListMyUserWatchlistsQuery | null = cache.readQuery(
          {
            query: WatchlistListMyUserWatchlistsDocument,
          }
        );
        if (
          !dataCache ||
          !dataCache.removePlayerFromWatchlist ||
          errors ||
          !data
        ) {
          return;
        }
        changeStatusPlayerInWatchlist(cache, playerDetail?.id ?? "", data);
        addOrRemovePlayerInWatchlist(
          cache,
          dataCache?.removePlayerFromWatchlist?.id,
          playerDetail?.id ?? "",
          "remove"
        );
      },
    });
  };

  return { deletePlayer };
};

export const useDeleteRecommendationPlayer = () => {
  const infoTemp = useReactiveVar(playerDetailVar);
  const playerDetail = infoTemp?.player;
  const [removePlayerRecommendation] = useRemovePlayerRecommendationMutation();
  const recordRemovePlayerFromRecommendationsGoal = useRecordUserGoal({
    goalType: GoalType.RemovePlayerFromRecommendations,
    value: playerDetail?.id ?? "",
  });

  const deleteRecommendationPlayer = () => {
    removePlayerRecommendation({
      variables: { playerId: playerDetail?.id ?? "" },
      refetchQueries: ["RecommendationsListPlayerRecommendations"],
      optimisticResponse: {
        __typename: "Mutation",
        removePlayerFromRecommendations: {
          __typename: "Player",
          ...((playerDetail ?? {}) as WatchlistPlayerFragment),
        },
      },
      update: (cache, { data: updateData, errors }) => {
        const playerId = updateData?.removePlayerFromRecommendations?.id;
        if (!!infoTemp?.infoRecommendation?.id && !!playerId && !errors) {
          cache.modify({
            id: cache.identify({ __typename: "Query", id: "ROOT_QUERY" }),
            fields: {
              [`listPlayerRecommendations({"recommendationType":"${infoTemp?.infoRecommendation?.type}"})`](
                existingData
              ) {
                const data = existingData as { __ref: string }[];
                return data.filter(
                  item =>
                    item.__ref !==
                    `PlayerRecommendationResult:${infoTemp?.infoRecommendation?.id}`
                );
              },
            },
          });
        }
      },
      onCompleted: () => {
        recordRemovePlayerFromRecommendationsGoal();
        playerDetailVar(undefined);
      },
    });
  };

  return { deleteRecommendationPlayer };
};
